/**
 * AI Summarization Service
 * Client-side service for communicating with the AI summarization API
 */

import { protectedResources } from '../config/msalConfig';

const API_BASE = protectedResources.api.baseEndPoint;

/**
 * Generate AI summary for a report with SSE streaming
 * @param {Object} params - Request parameters
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Function} onComplete - Callback when complete
 * @param {Function} onError - Callback for errors
 * @param {string} accessToken - Bearer token
 * @returns {AbortController} Controller to cancel the request
 */
export function generateSummaryStream({
    reportId,
    pageId,
    extractedData,
    options = {},
    organizationId,
    reportName,
    pbiToken
}, accessToken, { onChunk, onStatus, onComplete, onError }) {
    const controller = new AbortController();

    const fetchSSE = async () => {
        try {
            const response = await fetch(
                `${API_BASE}/v1/summarize/report`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                        'Accept': 'text/event-stream'
                    },
                    body: JSON.stringify({
                        reportId,
                        pageId,
                        extractedData,
                        options,
                        organizationId,
                        reportName,
                        pbiToken
                    }),
                    signal: controller.signal
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                // Process complete events from buffer
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                let currentEvent = null;
                let currentData = '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.substring(7).trim();
                    } else if (line.startsWith('data: ')) {
                        currentData = line.substring(6);

                        if (currentEvent && currentData) {
                            try {
                                const data = JSON.parse(currentData);
                                handleSSEEvent(currentEvent, data, {
                                    onChunk,
                                    onStatus,
                                    onComplete,
                                    onError
                                });
                            } catch (parseError) {
                                // Handle non-JSON data
                                handleSSEEvent(currentEvent, currentData, {
                                    onChunk,
                                    onStatus,
                                    onComplete,
                                    onError
                                });
                            }
                        }

                        currentEvent = null;
                        currentData = '';
                    }
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Summary generation cancelled');
                return;
            }

            console.error('SSE Error:', error);
            onError?.(error);
        }
    };

    fetchSSE();

    return controller;
}

/**
 * Handle SSE events
 */
function handleSSEEvent(eventType, data, callbacks) {
    const { onChunk, onStatus, onComplete, onError } = callbacks;

    switch (eventType) {
        case 'chunk':
            onChunk?.(data.content || data);
            break;
        case 'status':
            onStatus?.(data.message || data);
            break;
        case 'complete':
            onComplete?.(data);
            break;
        case 'error':
            onError?.(new Error(data.message || 'Unknown error'));
            break;
        default:
            console.warn('Unknown SSE event:', eventType);
    }
}

/**
 * Generate AI summary synchronously (non-streaming)
 * @param {Object} params - Request parameters
 * @param {string} accessToken - Bearer token
 * @returns {Promise<Object>} Summary result
 */
export async function generateSummarySync({
    reportId,
    pageId,
    extractedData,
    options = {},
    organizationId,
    reportName,
    pbiToken
}, accessToken) {
    const response = await fetch(
        `${API_BASE}/v1/summarize/report/sync`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
                reportId,
                pageId,
                extractedData,
                options,
                organizationId,
                reportName,
                pbiToken
            })
        }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP ${response.status}`);
    }

    return data.data;
}

/**
 * Get cached summary for a report
 * @param {string} reportId - Report ID
 * @param {string} pageId - Page ID (optional)
 * @param {string} organizationId - Organization ID
 * @param {string} accessToken - Bearer token
 * @returns {Promise<Object|null>} Cached summary or null
 */
export async function getCachedSummary(reportId, pageId, organizationId, accessToken) {
    const params = new URLSearchParams();
    if (pageId) params.append('pageId', pageId);
    if (organizationId) params.append('organizationId', organizationId);

    const url = `${API_BASE}/v1/summarize/report/${reportId}?${params.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
    }

    return data.data;
}

/**
 * Submit feedback for a summary
 * @param {Object} feedback - Feedback data
 * @param {string} accessToken - Bearer token
 * @returns {Promise<Object>} Feedback result
 */
export async function submitFeedback({
    summaryId,
    helpful,
    rating,
    comment,
    feedbackType,
    organizationId
}, accessToken) {
    const response = await fetch(
        `${API_BASE}/v1/summarize/feedback`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
                summaryId,
                helpful,
                rating,
                comment,
                feedbackType,
                organizationId
            })
        }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP ${response.status}`);
    }

    return data.data;
}

/**
 * Get current rate limit status
 * @param {string} accessToken - Bearer token
 * @returns {Promise<Object>} Rate limit status
 */
export async function getRateLimitStatus(accessToken) {
    const response = await fetch(
        `${API_BASE}/v1/summarize/rate-limit`,
        {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP ${response.status}`);
    }

    return data.data;
}

/**
 * Process AI Copilot query
 * @param {Object} params - Query parameters
 * @param {string} accessToken - Bearer token
 * @returns {Promise<Object>} Response with answer and sources
 */
export async function processAICopilotQuery({ query, userId, organizationId, conversationId }, accessToken) {
    const response = await fetch(
        `${API_BASE}/v1/copilot/query`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
                query,
                organizationId,
                conversationId,
                streaming: false
            })
        }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP ${response.status}`);
    }

    return data;
}

/**
 * Get suggested questions
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Suggested questions
 */
export async function getSuggestedQuestions({ userId, organizationId, conversationId }) {
    // Return empty array for now since backend endpoint may not be ready
    // TODO: Implement when backend endpoint is available
    return { data: { questions: [] } };
}

/**
 * Save conversation message
 * @param {Object} params - Message parameters
 * @returns {Promise<Object>} Save result
 */
export async function saveConversationMessage({ conversationId, role, content, sources, timestamp }) {
    // Stub for now - messages are saved via processQuery
    return { success: true };
}

export default {
    generateSummaryStream,
    generateSummarySync,
    getCachedSummary,
    processAICopilotQuery,
    getSuggestedQuestions,
    saveConversationMessage,
    submitFeedback,
    getRateLimitStatus
};
